import React, { useState } from 'react'
import {
  ShieldCheck,
  TrendingUp,
  Building2,
  Landmark,
  Award,
  Cpu,
  Lock,
  CheckCircle2,
  ChevronDown,
  ArrowRight,
  PieChart,
  BarChart3,
  Phone,
  Mail,
  Sparkles,
  Globe2,
  Check,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { resolveImageUrl } from '@/utils/imageUrl'
import type { CustomLandingPageProps } from '../registry/CustomLandingRegistry'

export const VanguardCustomLanding: React.FC<CustomLandingPageProps> = ({
  branding,
  loginUrl,
  contactEmail,
  contactPhone,
  onOpenDemoModal,
}) => {
  const [logoError, setLogoError] = useState<boolean>(false)
  const brandName = branding?.brand_name || 'Vanguard Gestão'
  const resolvedLogoUrl = resolveImageUrl(branding?.logo_light_url)
  const shouldRenderImage = !logoError && Boolean(resolvedLogoUrl)
  const email = contactEmail || 'governanca@vanguardgestao.com.br'
  const phone = contactPhone || '+55 (11) 3168-4500'


  const primaryColor = branding?.primary_color || 'var(--primary)'
  const primaryHover = branding?.primary_hover || 'var(--primary-hover)'
  const primaryLight = branding?.primary_light || 'var(--primary-light)'
  const primaryGlow = branding?.primary_glow || 'rgba(193, 158, 115, 0.25)'

  const customColorsStyle = {
    '--primary': primaryColor,
    '--primary-hover': primaryHover,
    '--primary-light': primaryLight,
    '--primary-glow': primaryGlow,
    ...(branding?.background_color ? { '--bg': branding.background_color } : {}),
    ...(branding?.surface_color ? { '--surface': branding.surface_color } : {}),
    ...(branding?.surface_secondary ? { '--surface-2': branding.surface_secondary } : {}),
    ...(branding?.border_color ? { '--border': branding.border_color } : {}),
    ...(branding?.text_primary ? { '--text-1': branding.text_primary } : {}),
    ...(branding?.text_secondary ? { '--text-2': branding.text_secondary } : {}),
    ...(branding?.text_on_primary ? { '--text-on-primary': branding.text_on_primary } : {}),
  } as React.CSSProperties & Record<string, string | number | undefined>

  // FAQ Accordion State
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null)

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index)
  }

  const handleWhatsApp = () => {
    if (!contactPhone) return
    const cleanPhone = contactPhone.replace(/\D/g, '')
    const msg = encodeURIComponent(
      `Olá! Gostaria de agendar uma apresentação executiva sobre os serviços de Multi-Family Office, Valuation e BPO Financeiro da ${brandName}.`
    )
    window.open(`https://wa.me/${cleanPhone}?text=${msg}`, '_blank', 'noopener,noreferrer')
  }

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault()
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  // FAQ Data
  const faqItems = [
    {
      question: 'Como a Vanguard Gestão se integra às instituições financeiras e custodiantes?',
      answer:
        'Nossa infraestrutura conecta-se diretamente a mais de 30 instituições bancárias, corretoras e custodiantes através de protocolos homologados pelo Open Finance Brasil (Banco Central) e leituras automáticas de extratos eletrônicos (OFX, CNAB e APIs criptografadas), garantindo conciliação automatizada D+0 sem intervenção manual e sem custódia de senhas transacionais.',
    },
    {
      question: 'Quais protocolos de sigilo, governança e conformidade LGPD são aplicados?',
      answer:
        'Todos os dados de participações societárias, saldos, ativos e movimentações são protegidos com criptografia de ponta a ponta AES-256 e TLS 1.3. A governança opera com isolamento rigoroso por organização (Row Level Security no PostgreSQL), acordos formais de confidencialidade (NDA) e conformidade estrita com a Lei Geral de Proteção de Dados (Lei 13.709/2018).',
    },
    {
      question: 'A plataforma atende estruturas societárias complexas com holdings e offshores?',
      answer:
        'Sim. A Vanguard Gestão foi desenhada especificamente para suportar grupos com múltiplas empresas, holdings patrimoniais puras e mistas, SPEs e estruturas internacionais com consolidação contábil multi-moeda (BRL, USD, EUR e CHF) e visão consolidada de patrimônio líquido em tempo real.',
    },
    {
      question: 'Qual é o modelo de atendimento e acompanhamento dos consultores seniores?',
      answer:
        'Cada cliente conta com uma equipe dedicada liderada por um Controller Sênior e um Gestor de Governança. Além do suporte diário para rotinas operacionais de pagamentos e conciliação, realizamos comitês mensais executivos de apresentação de DRE gerencial, fluxo de caixa projetado de 13 semanas e relatórios de rentabilidade patrimonial.',
    },
    {
      question: 'Qual é o prazo médio de transição e implementação da esteira de BPO?',
      answer:
        'Nosso processo de onboarding estruturado dura em média 4 semanas, desde o diagnóstico inicial e parametrização do plano de contas até a virada definitiva de chaves (go-live assistido), sem qualquer interrupção das rotinas bancárias e operacionais da sua empresa ou family office.',
    },
  ]

  return (
    <div
      className="min-h-screen bg-background text-foreground selection:bg-primary selection:text-text-on-primary"
      style={customColorsStyle}
      data-testid="vanguard-custom-landing"
    >
      {/* 1. NAVBAR INSTITUCIONAL FIXA */}
      <header className="sticky top-0 z-50 bg-surface/90 backdrop-blur-md border-b border-border">
        <div className="max-w-[1360px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 min-h-[36px]">
            {shouldRenderImage ? (
              <img
                src={resolvedLogoUrl}
                alt={brandName}
                onError={() => setLogoError(true)}
                className="h-9 w-auto max-h-9 max-w-[220px] object-contain transition-opacity duration-200"
                loading="eager"
                decoding="async"
              />
            ) : (
              <span className="font-display text-lg font-bold text-text-1 tracking-tight select-none">{brandName}</span>
            )}
            <span className="hidden sm:inline-block px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-[11px] font-bold tracking-wide border border-primary/25 uppercase">
              Multi-Family Office &amp; Gestão de Ativos
            </span>
          </div>

          {/* Links Âncora Desktop */}
          <nav className="hidden lg:flex items-center gap-6 text-xs font-semibold text-text-2">
            <a
              href="#solucoes"
              onClick={(e) => scrollToSection(e, 'solucoes')}
              className="hover:text-text-1 transition-colors"
            >
              Soluções
            </a>
            <a
              href="#showcase"
              onClick={(e) => scrollToSection(e, 'showcase')}
              className="hover:text-text-1 transition-colors"
            >
              Plataforma
            </a>
            <a
              href="#seguranca"
              onClick={(e) => scrollToSection(e, 'seguranca')}
              className="hover:text-text-1 transition-colors"
            >
              Segurança
            </a>
            <a
              href="#metodologia"
              onClick={(e) => scrollToSection(e, 'metodologia')}
              className="hover:text-text-1 transition-colors"
            >
              Metodologia
            </a>
            <a
              href="#faq"
              onClick={(e) => scrollToSection(e, 'faq')}
              className="hover:text-text-1 transition-colors"
            >
              FAQ
            </a>
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={loginUrl}
              className="text-xs font-semibold text-text-2 hover:text-text-1 transition-colors px-3 py-1.5"
            >
              Portal do Investidor
            </a>
            <Button
              type="button"
              variant="primary"
              onClick={onOpenDemoModal}
              style={{ fontSize: '12px', padding: '7px 14px' }}
            >
              Solicitar Diagnóstico
            </Button>
          </div>
        </div>
      </header>

      {/* 2. HERO SECTION DE ALTO IMPACTO */}
      <section className="py-20 lg:py-28 border-b border-border relative overflow-hidden bg-gradient-to-b from-surface/50 to-background">
        <div className="max-w-[1360px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/25 text-primary text-xs font-bold tracking-wider uppercase">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Inteligência Patrimonial &amp; BPO Financeiro Consultivo</span>
            </div>

            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-display font-bold text-text-1 tracking-tight leading-[1.08]">
              Governança Financeira de Alto Nível e Gestão de Ativos para Empresas e Family Offices
            </h1>

            <p className="text-base sm:text-lg text-text-2 font-normal leading-relaxed max-w-2xl">
              Consolidação de patrimônio em tempo real, automação de tesouraria multimoeda, compliance regulatório e relatórios executivos para tomada de decisão estratégica com padrão de auditoria independente.
            </p>

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button
                type="button"
                variant="primary"
                onClick={onOpenDemoModal}
                icon={<ArrowRight size={14} />}
                style={{ fontSize: '13px', padding: '11px 24px' }}
              >
                Solicitar Demonstração Exclusiva
              </Button>
              {contactPhone && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleWhatsApp}
                  icon={<Phone size={14} />}
                  style={{ fontSize: '13px', padding: '11px 22px' }}
                >
                  Falar no WhatsApp
                </Button>
              )}
            </div>

            {/* Garantias Institucionais */}
            <div className="flex flex-wrap gap-6 pt-4 text-xs text-text-3 border-t border-border/70">
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-primary" />
                <span>Conciliação D+0 Homologada</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Lock className="w-4 h-4 text-primary" />
                <span>Criptografia Bancária AES-256</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Building2 className="w-4 h-4 text-primary" />
                <span>Multi-Holdings &amp; Offshores</span>
              </div>
            </div>
          </div>

          {/* Painel de 3 Live Metrics Cards */}
          <div className="lg:col-span-5 space-y-4">
            <div className="p-6 rounded-2xl border border-border bg-surface shadow-lg space-y-5">
              <div className="flex items-center justify-between pb-3 border-b border-border">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-xs font-bold uppercase tracking-wider text-text-3">Indicadores de Custódia</span>
                </div>
                <span className="text-[11px] font-mono font-semibold text-primary">AUDITED METRICS</span>
              </div>

              {/* Card 1 */}
              <div className="p-4 rounded-xl border border-border/80 bg-surface-2 flex items-center justify-between">
                <div>
                  <span className="block text-[11px] text-text-3 font-medium">Patrimônio sob Supervisão e Acompanhamento</span>
                  <span className="text-2xl sm:text-3xl font-bold font-mono text-text-1">R$ 4.2 Bi+</span>
                </div>
                <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <TrendingUp className="w-5 h-5" />
                </div>
              </div>

              {/* Card 2 */}
              <div className="p-4 rounded-xl border border-border/80 bg-surface-2 flex items-center justify-between">
                <div>
                  <span className="block text-[11px] text-text-3 font-medium">Conciliação Diária Automatizada (18 Bancos)</span>
                  <span className="text-2xl sm:text-3xl font-bold font-mono text-text-1">100% D+0</span>
                </div>
                <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <ShieldCheck className="w-5 h-5" />
                </div>
              </div>

              {/* Card 3 */}
              <div className="p-4 rounded-xl border border-border/80 bg-surface-2 flex items-center justify-between">
                <div>
                  <span className="block text-[11px] text-text-3 font-medium">Disponibilidade e Integridade Criptografada</span>
                  <span className="text-2xl sm:text-3xl font-bold font-mono text-text-1">99.98% SLA</span>
                </div>
                <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <Lock className="w-5 h-5" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. BARRA DE CONFIANÇA E AUTORIDADE */}
      <section className="py-8 bg-surface-2 border-b border-border">
        <div className="max-w-[1360px] mx-auto px-6">
          <p className="text-center text-xs font-bold uppercase tracking-wider text-text-3 mb-6">
            Conformidade Regulatória, Segurança e Padrões Internacionais de Governança
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="p-3.5 rounded-xl border border-border/60 bg-surface text-center space-y-1">
              <div className="flex justify-center text-primary"><Landmark className="w-5 h-5" /></div>
              <span className="block text-xs font-bold text-text-1">CVM &amp; BACEN</span>
              <span className="block text-[10px] text-text-3">Open Finance Regulamentado</span>
            </div>
            <div className="p-3.5 rounded-xl border border-border/60 bg-surface text-center space-y-1">
              <div className="flex justify-center text-primary"><Award className="w-5 h-5" /></div>
              <span className="block text-xs font-bold text-text-1">Big Four Standard</span>
              <span className="block text-[10px] text-text-3">Plano de Contas Auditável</span>
            </div>
            <div className="p-3.5 rounded-xl border border-border/60 bg-surface text-center space-y-1">
              <div className="flex justify-center text-primary"><Lock className="w-5 h-5" /></div>
              <span className="block text-xs font-bold text-text-1">Criptografia AES-256</span>
              <span className="block text-[10px] text-text-3">Segurança em Repouso e Trânsito</span>
            </div>
            <div className="p-3.5 rounded-xl border border-border/60 bg-surface text-center space-y-1">
              <div className="flex justify-center text-primary"><ShieldCheck className="w-5 h-5" /></div>
              <span className="block text-xs font-bold text-text-1">Privacidade &amp; LGPD</span>
              <span className="block text-[10px] text-text-3">Segregação Total de Dados</span>
            </div>
            <div className="p-3.5 rounded-xl border border-border/60 bg-surface text-center space-y-1 col-span-2 sm:col-span-1">
              <div className="flex justify-center text-primary"><Globe2 className="w-5 h-5" /></div>
              <span className="block text-xs font-bold text-text-1">Multi-Moeda Global</span>
              <span className="block text-[10px] text-text-3">BRL, USD, EUR e CHF</span>
            </div>
          </div>
        </div>
      </section>

      {/* 4. OS 4 PILARES ESTRATÉGICOS */}
      <section id="solucoes" className="py-20 lg:py-28 border-b border-border bg-surface/30">
        <div className="max-w-[1360px] mx-auto px-6 space-y-12">
          <div className="text-center max-w-3xl mx-auto space-y-4">
            <span className="text-xs font-bold uppercase tracking-wider text-primary">Soluções Corporativas Integradas</span>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-text-1 tracking-tight">
              Os 4 Pilares da Metodologia Vanguard Gestão
            </h2>
            <p className="text-sm sm:text-base text-text-2">
              Da terceirização contínua das rotinas financeiras à inteligência de valuation para comitês executivos e conselhos de administração.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Pilar 1 */}
            <div className="p-8 rounded-2xl border border-border bg-surface hover:border-primary/40 transition-all duration-300 space-y-4 relative group">
              <div className="flex items-center justify-between">
                <span className="font-mono text-3xl font-bold text-primary/40 group-hover:text-primary transition-colors">01</span>
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <BarChart3 className="w-5 h-5" />
                </div>
              </div>
              <h3 className="text-xl font-display font-bold text-text-1">Controladoria &amp; BPO Financeiro Executivo</h3>
              <p className="text-xs sm:text-sm text-text-2 leading-relaxed">
                Terceirização integral das rotinas de contas a pagar, contas a receber, faturamento e conciliação bancária diária com SLA rigoroso em D+0.
              </p>
              <ul className="space-y-2.5 pt-2 text-xs text-text-2 border-t border-border">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                  <span>Conferência de extratos em múltiplos bancos e adquirentes de cartão</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                  <span>Emissão de relatórios de DRE gerencial e DFC mensal auditável</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                  <span>Classificação contábil analítica por centros de custo e projetos</span>
                </li>
              </ul>
            </div>

            {/* Pilar 2 */}
            <div className="p-8 rounded-2xl border border-border bg-surface hover:border-primary/40 transition-all duration-300 space-y-4 relative group">
              <div className="flex items-center justify-between">
                <span className="font-mono text-3xl font-bold text-primary/40 group-hover:text-primary transition-colors">02</span>
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <TrendingUp className="w-5 h-5" />
                </div>
              </div>
              <h3 className="text-xl font-display font-bold text-text-1">Gestão de Tesouraria &amp; Liquidez Estratégica</h3>
              <p className="text-xs sm:text-sm text-text-2 leading-relaxed">
                Centralização de tesouraria corporativa, otimização de caixas livres (cash pooling) e projeção de fluxo de caixa contínuo para evitar ociosidade de capital.
              </p>
              <ul className="space-y-2.5 pt-2 text-xs text-text-2 border-t border-border">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                  <span>Projeção de fluxo de caixa rolling forecast de 13 semanas</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                  <span>Acompanhamento de linhas de crédito, taxas de juros e covenants</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                  <span>Gestão de liquidez para aportes, distribuições de dividendos e pró-labore</span>
                </li>
              </ul>
            </div>

            {/* Pilar 3 */}
            <div className="p-8 rounded-2xl border border-border bg-surface hover:border-primary/40 transition-all duration-300 space-y-4 relative group">
              <div className="flex items-center justify-between">
                <span className="font-mono text-3xl font-bold text-primary/40 group-hover:text-primary transition-colors">03</span>
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <PieChart className="w-5 h-5" />
                </div>
              </div>
              <h3 className="text-xl font-display font-bold text-text-1">Inteligência de Valuation &amp; Monitoramento de Ativos</h3>
              <p className="text-xs sm:text-sm text-text-2 leading-relaxed">
                Consolidação patrimonial com marcação a mercado de participações societárias, ativos ilíquidos, carteiras de investimentos e fundos fechados.
              </p>
              <ul className="space-y-2.5 pt-2 text-xs text-text-2 border-t border-border">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                  <span>Acompanhamento contínuo de múltiplos de EBITDA e valuation de empresas</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                  <span>Consolidação de custódias bancárias nacionais e internacionais</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                  <span>Relatórios executivos de rentabilidade ponderada (TWR e MWR)</span>
                </li>
              </ul>
            </div>

            {/* Pilar 4 */}
            <div className="p-8 rounded-2xl border border-border bg-surface hover:border-primary/40 transition-all duration-300 space-y-4 relative group">
              <div className="flex items-center justify-between">
                <span className="font-mono text-3xl font-bold text-primary/40 group-hover:text-primary transition-colors">04</span>
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <Building2 className="w-5 h-5" />
                </div>
              </div>
              <h3 className="text-xl font-display font-bold text-text-1">Compliance Tributário &amp; Governança de Holdings</h3>
              <p className="text-xs sm:text-sm text-text-2 leading-relaxed">
                Segurança fiscal e suporte integral à estruturação patrimonial familiar, planejamento sucessório e conformidade de regras contábeis internacionais (IFRS).
              </p>
              <ul className="space-y-2.5 pt-2 text-xs text-text-2 border-t border-border">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                  <span>Parametrização e governança de transações intercompany</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                  <span>Auditoria permanente para prevenção de passivos tributários</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                  <span>Reuniões mensais com consultor sênior dedicado e comitê consultivo</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 5. SHOWCASE ANALÍTICO DA PLATAFORMA (DASHBOARD MOCKUP) */}
      <section id="showcase" className="py-20 lg:py-28 border-b border-border bg-background">
        <div className="max-w-[1360px] mx-auto px-6 space-y-12">
          <div className="text-center max-w-3xl mx-auto space-y-4">
            <span className="text-xs font-bold uppercase tracking-wider text-primary">Visão Executiva em Tempo Real</span>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-text-1 tracking-tight">
              A Plataforma Analítica da Vanguard Gestão
            </h2>
            <p className="text-sm sm:text-base text-text-2">
              Interface consolidada para controle de liquidez, conciliação e indicadores financeiros de todas as empresas do grupo em um único acesso restrito.
            </p>
          </div>

          {/* Terminal Mockup */}
          <div className="p-6 lg:p-8 rounded-3xl border border-border bg-surface shadow-2xl space-y-6">
            {/* Header do Terminal */}
            <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-red-500/80" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                <div className="w-3 h-3 rounded-full bg-emerald-500/80" />
                <span className="text-xs font-mono text-text-3 ml-2">vanguard-terminal-pro // v2.8.4</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1 rounded-lg bg-surface-2 border border-border text-xs font-semibold text-text-2">
                <Building2 size={13} className="text-primary" />
                <span>Holding Alpha Participações S.A. (Consolidado 6 Empresas)</span>
              </div>
            </div>

            {/* KPIs do Mockup */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl border border-border bg-surface-2 space-y-1">
                <span className="text-[11px] text-text-3 font-medium">Liquidez Total Disponível</span>
                <span className="block text-xl font-bold font-mono text-text-1">R$ 18.450.280,00</span>
                <span className="text-[10px] text-emerald-500 font-semibold">+14.2% vs mês anterior</span>
              </div>
              <div className="p-4 rounded-xl border border-border bg-surface-2 space-y-1">
                <span className="text-[11px] text-text-3 font-medium">EBITDA Gerencial Consolidado</span>
                <span className="block text-xl font-bold font-mono text-text-1">R$ 4.820.000,00</span>
                <span className="text-[10px] text-text-3">Margem Operacional: 28.4%</span>
              </div>
              <div className="p-4 rounded-xl border border-border bg-surface-2 space-y-1">
                <span className="text-[11px] text-text-3 font-medium">Contas a Pagar / Semana Atual</span>
                <span className="block text-xl font-bold font-mono text-text-1">R$ 1.240.500,00</span>
                <span className="text-[10px] text-emerald-500 font-semibold">100% Programado com Saldo</span>
              </div>
              <div className="p-4 rounded-xl border border-border bg-surface-2 space-y-1">
                <span className="text-[11px] text-text-3 font-medium">Divergências de Conciliação</span>
                <span className="block text-xl font-bold font-mono text-emerald-500">0 Pendências</span>
                <span className="text-[10px] text-text-3">Status: Conciliação D+0 Fechada</span>
              </div>
            </div>

            {/* Linhas da Tabela Simulada */}
            <div className="p-4 rounded-xl border border-border bg-surface-2 overflow-x-auto">
              <div className="min-w-[600px] space-y-3 text-xs">
                <div className="grid grid-cols-5 text-text-3 font-bold uppercase tracking-wider pb-2 border-b border-border">
                  <span>Instituição</span>
                  <span>Conta / Carteira</span>
                  <span>Saldo Disponível</span>
                  <span>Conciliação</span>
                  <span>Última Transação</span>
                </div>
                <div className="grid grid-cols-5 items-center text-text-2 font-medium">
                  <span className="font-semibold text-text-1">Itaú Personnalité</span>
                  <span>Conta Movimento 01</span>
                  <span className="font-mono text-text-1">R$ 6.240.180,00</span>
                  <span className="inline-flex items-center gap-1 text-emerald-500 font-semibold"><Check size={12} /> D+0 OK</span>
                  <span className="text-text-3">Hoje, 17:42</span>
                </div>
                <div className="grid grid-cols-5 items-center text-text-2 font-medium">
                  <span className="font-semibold text-text-1">BTG Pactual Banking</span>
                  <span>Conta Tesouraria 02</span>
                  <span className="font-mono text-text-1">R$ 8.910.000,00</span>
                  <span className="inline-flex items-center gap-1 text-emerald-500 font-semibold"><Check size={12} /> D+0 OK</span>
                  <span className="text-text-3">Hoje, 16:15</span>
                </div>
                <div className="grid grid-cols-5 items-center text-text-2 font-medium">
                  <span className="font-semibold text-text-1">Safra Private</span>
                  <span>Conta Custódia Alpha</span>
                  <span className="font-mono text-text-1">R$ 3.300.100,00</span>
                  <span className="inline-flex items-center gap-1 text-emerald-500 font-semibold"><Check size={12} /> D+0 OK</span>
                  <span className="text-text-3">Hoje, 15:30</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. SEGURANÇA DE NÍVEL BANCÁRIO & INFRAESTRUTURA */}
      <section id="seguranca" className="py-20 lg:py-28 border-b border-border bg-surface-2">
        <div className="max-w-[1360px] mx-auto px-6 space-y-12">
          <div className="text-center max-w-3xl mx-auto space-y-4">
            <span className="text-xs font-bold uppercase tracking-wider text-primary">Blindagem Tecnológica</span>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-text-1 tracking-tight">
              Segurança Institucional e Proteção de Dados Patrimoniais
            </h2>
            <p className="text-sm sm:text-base text-text-2">
              Infraestrutura de ponta desenvolvida para atender aos mais rigorosos critérios de compliance, auditoria e privacidade da informação.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-6 rounded-2xl border border-border bg-surface space-y-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                <Lock className="w-5 h-5" />
              </div>
              <h4 className="text-base font-bold text-text-1">Criptografia AES-256</h4>
              <p className="text-xs text-text-2 leading-relaxed">
                Dados protegidos com chaves de 256 bits em repouso e transmissão via TLS 1.3 com rotação periódica e chaves segregadas por organização.
              </p>
            </div>

            <div className="p-6 rounded-2xl border border-border bg-surface space-y-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h4 className="text-base font-bold text-text-1">Isolamento Multi-Tenant</h4>
              <p className="text-xs text-text-2 leading-relaxed">
                Segregação estrita no nível do PostgreSQL através de Row Level Security (RLS), impedindo qualquer acesso cruzado não autorizado.
              </p>
            </div>

            <div className="p-6 rounded-2xl border border-border bg-surface space-y-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                <Landmark className="w-5 h-5" />
              </div>
              <h4 className="text-base font-bold text-text-1">Open Finance Brasil</h4>
              <p className="text-xs text-text-2 leading-relaxed">
                Conexão homologada segundo as diretrizes de segurança do Banco Central, garantindo integridade e leitura passiva sem risco transacional.
              </p>
            </div>

            <div className="p-6 rounded-2xl border border-border bg-surface space-y-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                <Cpu className="w-5 h-5" />
              </div>
              <h4 className="text-base font-bold text-text-1">Inteligência Preditiva</h4>
              <p className="text-xs text-text-2 leading-relaxed">
                Modelos algorítmicos para auditoria em tempo real, detecção de desvios orçamentários e prevenção de cobranças em duplicidade.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 7. METODOLOGIA DE ONBOARDING EM 4 FASES */}
      <section id="metodologia" className="py-20 lg:py-28 border-b border-border bg-surface/30">
        <div className="max-w-[1360px] mx-auto px-6 space-y-12">
          <div className="text-center max-w-3xl mx-auto space-y-4">
            <span className="text-xs font-bold uppercase tracking-wider text-primary">Implementação Estruturada</span>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-text-1 tracking-tight">
              Metodologia de Onboarding em 4 Fases
            </h2>
            <p className="text-sm sm:text-base text-text-2">
              Processo ágil e confidencial de 4 semanas com go-live assistido sem interromper as atividades da sua organização.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-6 rounded-2xl border border-border bg-surface space-y-3 relative">
              <span className="text-xs font-mono font-bold text-primary">FASE 01 // SEMANA 1</span>
              <h4 className="text-base font-bold text-text-1">Diagnóstico Patrimonial</h4>
              <p className="text-xs text-text-2 leading-relaxed">
                Mapeamento das contas bancárias, custodiantes, fluxo de aprovação de pagamentos e plano de contas atual.
              </p>
            </div>

            <div className="p-6 rounded-2xl border border-border bg-surface space-y-3 relative">
              <span className="text-xs font-mono font-bold text-primary">FASE 02 // SEMANA 2</span>
              <h4 className="text-base font-bold text-text-1">Integração &amp; Open Finance</h4>
              <p className="text-xs text-text-2 leading-relaxed">
                Conexão das instituições financeiras, parametrização dos canais de importação de extratos e carga histórica contábil.
              </p>
            </div>

            <div className="p-6 rounded-2xl border border-border bg-surface space-y-3 relative">
              <span className="text-xs font-mono font-bold text-primary">FASE 03 // SEMANA 3</span>
              <h4 className="text-base font-bold text-text-1">Parametrização de Regras</h4>
              <p className="text-xs text-text-2 leading-relaxed">
                Configuração da esteira de conciliação D+0, centros de custo, relatórios executivos e testes de fluxo financeiro.
              </p>
            </div>

            <div className="p-6 rounded-2xl border border-border bg-surface space-y-3 relative">
              <span className="text-xs font-mono font-bold text-primary">FASE 04 // SEMANA 4</span>
              <h4 className="text-base font-bold text-text-1">Governança Permanente</h4>
              <p className="text-xs text-text-2 leading-relaxed">
                Operação plena assistida, entrega dos primeiros relatórios de fechamento e início dos comitês executivos mensais.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 8. DEPOIMENTOS E CASOS DE SUCESSO */}
      <section className="py-20 lg:py-28 border-b border-border bg-background">
        <div className="max-w-[1360px] mx-auto px-6 space-y-12">
          <div className="text-center max-w-3xl mx-auto space-y-4">
            <span className="text-xs font-bold uppercase tracking-wider text-primary">Validação Executiva</span>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-text-1 tracking-tight">
              O que Dizem os Líderes que Confiam na Vanguard
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-6 rounded-2xl border border-border bg-surface space-y-4 flex flex-col justify-between">
              <p className="text-xs sm:text-sm text-text-2 italic leading-relaxed">
                &ldquo;A consolidação de 14 empresas do nosso grupo em um único painel reduziu nosso fechamento financeiro de 12 dias para menos de 24 horas, com conciliação D+0 impecável.&rdquo;
              </p>
              <div className="pt-4 border-t border-border">
                <span className="block text-xs font-bold text-text-1">Eduardo Mendonça</span>
                <span className="block text-[11px] text-text-3">CFO — Grupo Industrial e Logístico</span>
              </div>
            </div>

            <div className="p-6 rounded-2xl border border-border bg-surface space-y-4 flex flex-col justify-between">
              <p className="text-xs sm:text-sm text-text-2 italic leading-relaxed">
                &ldquo;A precisão na marcação a mercado de participações societárias e ativos ilíquidos trouxe a transparência e segurança que os herdeiros do family office exigiam.&rdquo;
              </p>
              <div className="pt-4 border-t border-border">
                <span className="block text-xs font-bold text-text-1">Beatriz Alencar</span>
                <span className="block text-[11px] text-text-3">Gestora Patrimonial — Family Office Multi-Setorial</span>
              </div>
            </div>

            <div className="p-6 rounded-2xl border border-border bg-surface space-y-4 flex flex-col justify-between">
              <p className="text-xs sm:text-sm text-text-2 italic leading-relaxed">
                &ldquo;A esteira de BPO financeiro assumiu toda a complexidade operacional de pagamentos e cobranças, liberando nossa diretoria para focar em novas aquisições de M&amp;A.&rdquo;
              </p>
              <div className="pt-4 border-t border-border">
                <span className="block text-xs font-bold text-text-1">Rodrigo Carvalho</span>
                <span className="block text-[11px] text-text-3">Diretor Executivo — Holding de Serviços Corporativos</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 9. FAQ INTERATIVO (ACORDEÃO EM REACT STATE) */}
      <section id="faq" className="py-20 lg:py-28 border-b border-border bg-surface-2">
        <div className="max-w-[900px] mx-auto px-6 space-y-10">
          <div className="text-center space-y-4">
            <span className="text-xs font-bold uppercase tracking-wider text-primary">Perguntas Frequentes</span>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-text-1 tracking-tight">
              Esclarecimentos Técnicos e Operacionais
            </h2>
            <p className="text-sm text-text-2">
              Tudo o que você precisa saber sobre a segurança, implementação e rotinas de governança da Vanguard Gestão.
            </p>
          </div>

          <div className="space-y-3" data-testid="faq-accordion">
            {faqItems.map((item, index) => {
              const isOpen = openFaqIndex === index
              return (
                <div
                  key={index}
                  className="rounded-xl border border-border bg-surface transition-colors duration-200 overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => toggleFaq(index)}
                    aria-expanded={isOpen}
                    className="w-full p-5 text-left flex items-center justify-between gap-4 font-semibold text-sm sm:text-base text-text-1 hover:text-primary transition-colors"
                  >
                    <span>{item.question}</span>
                    <ChevronDown
                      size={18}
                      className={`text-primary shrink-0 transition-transform duration-200 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 pt-1 text-xs sm:text-sm text-text-2 leading-relaxed border-t border-border/50">
                      {item.answer}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* 10. CTA FINAL & RODAPÉ INSTITUCIONAL COMPLETO */}
      <section className="py-20 bg-gradient-to-t from-surface to-background">
        <div className="max-w-[1360px] mx-auto px-6">
          <div className="p-10 sm:p-16 rounded-3xl border border-primary/30 bg-surface shadow-2xl text-center space-y-6 max-w-4xl mx-auto">
            <span className="text-xs font-bold uppercase tracking-wider text-primary">Inicie sua Jornada de Governança</span>
            <h2 className="text-3xl sm:text-5xl font-display font-bold text-text-1 tracking-tight">
              Pronto para elevar a governança financeira da sua holding ao próximo nível?
            </h2>
            <p className="text-sm sm:text-base text-text-2 max-w-2xl mx-auto">
              Agende uma apresentação consultiva com nossos diretores e receba um diagnóstico patrimonial confidencial sem custo.
            </p>
            <div className="flex flex-wrap justify-center items-center gap-4 pt-4">
              <Button
                type="button"
                variant="primary"
                onClick={onOpenDemoModal}
                icon={<ArrowRight size={14} />}
                style={{ fontSize: '13px', padding: '12px 28px' }}
              >
                Solicitar Apresentação Executiva
              </Button>
              {contactPhone && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleWhatsApp}
                  icon={<Phone size={14} />}
                  style={{ fontSize: '13px', padding: '12px 24px' }}
                >
                  Falar no WhatsApp
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER CORPORATIVO EM 4 COLUNAS */}
      <footer className="bg-surface border-t border-border py-14">
        <div className="max-w-[1360px] mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="space-y-4">
            <span className="font-display text-lg font-bold text-text-1 tracking-tight">{brandName}</span>
            <p className="text-xs text-text-3 leading-relaxed">
              Multi-Family Office, inteligência de valuation e terceirização de controladoria financeira executiva para holdings e empresas de alto padrão.
            </p>
            <span className="inline-block text-[11px] font-mono text-primary">
              Segurança Bancária AES-256 // SLA D+0
            </span>
          </div>

          <div className="space-y-3">
            <span className="text-xs font-bold uppercase tracking-wider text-text-1">Soluções</span>
            <ul className="space-y-2 text-xs text-text-3">
              <li><a href="#solucoes" className="hover:text-text-1 transition-colors">Controladoria &amp; BPO Executivo</a></li>
              <li><a href="#solucoes" className="hover:text-text-1 transition-colors">Gestão de Tesouraria &amp; Liquidez</a></li>
              <li><a href="#solucoes" className="hover:text-text-1 transition-colors">Valuation &amp; Inteligência de Ativos</a></li>
              <li><a href="#solucoes" className="hover:text-text-1 transition-colors">Compliance de Holdings Familiares</a></li>
            </ul>
          </div>

          <div className="space-y-3">
            <span className="text-xs font-bold uppercase tracking-wider text-text-1">Governança</span>
            <ul className="space-y-2 text-xs text-text-3">
              <li><a href="#seguranca" className="hover:text-text-1 transition-colors">Política de Privacidade &amp; LGPD</a></li>
              <li><a href="#seguranca" className="hover:text-text-1 transition-colors">Termos de Uso e Confidencialidade</a></li>
              <li><a href="#seguranca" className="hover:text-text-1 transition-colors">Protocolos Open Finance</a></li>
              <li><a href={loginUrl} className="hover:text-text-1 transition-colors">Acesso ao Portal do Investidor</a></li>
            </ul>
          </div>

          <div className="space-y-3">
            <span className="text-xs font-bold uppercase tracking-wider text-text-1">Contato Executivo</span>
            <div className="space-y-2 text-xs text-text-3">
              <div className="flex items-center gap-2">
                <Mail size={13} className="text-primary" />
                <span>{email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone size={13} className="text-primary" />
                <span>{phone}</span>
              </div>
              <div className="pt-2">
                <Button
                  type="button"
                  variant="primary"
                  onClick={onOpenDemoModal}
                  style={{ width: '100%', fontSize: '11px', padding: '6px 12px' }}
                >
                  Agendar Reunião
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-[1360px] mx-auto px-6 pt-10 mt-10 border-t border-border/60 flex flex-wrap items-center justify-between gap-4 text-xs text-text-3">
          <span>&copy; {new Date().getFullYear()} {brandName}. Todos os direitos reservados.</span>
          <span>Plataforma White-Label homologada Seven Analytics // Enterprise Governance</span>
        </div>
      </footer>
    </div>
  )
}
